import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE } from '../../config';

interface UserItem {
  username: string;
  email: string;
  displayName: string;
  picture: string;
  status: string;
  enabled: boolean;
  createdAt: string;
  lastLogin: string;
  provider: string;
}

export function AdminUsers() {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await authFetch(`${API_BASE}/v1/admin/users`);
        if (res.ok) {
          const json = await res.json();
          setUsers(json.data || []);
        }
      } catch { /* */ }
      finally { setLoading(false); }
    }
    fetchUsers();
  }, [authFetch]);

  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.email.toLowerCase().includes(q) ||
      u.displayName.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q);
  });

  const totalUsers = users.length;
  const googleUsers = users.filter((u) => u.provider === 'Google').length;
  const appleUsers = users.filter((u) => u.provider === 'Apple').length;
  const emailUsers = users.filter((u) => u.provider === 'Email').length;

  if (loading) return <div className="admin-loading">Loading users...</div>;

  return (
    <div>
      <h1>Users</h1>

      <div className="admin-stats" style={{ marginBottom: '24px' }}>
        <div className="admin-stat-card">
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{totalUsers}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Google</div>
          <div className="stat-value">{googleUsers}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Apple</div>
          <div className="stat-value">{appleUsers}</div>
        </div>
        <div className="admin-stat-card">
          <div className="stat-label">Email</div>
          <div className="stat-value">{emailUsers}</div>
        </div>
      </div>

      <div className="admin-search-bar" style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users by name or email..."
          className="admin-search-input"
        />
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Provider</th>
              <th>Status</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.username}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {user.picture ? (
                      <img src={user.picture} alt="" style={{ width: 28, height: 28, borderRadius: 14 }} referrerPolicy="no-referrer" />
                    ) : (
                      <div style={{ width: 28, height: 28, borderRadius: 14, background: 'var(--color-bg-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                        {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span style={{ fontWeight: 500 }}>{user.displayName || '—'}</span>
                  </div>
                </td>
                <td style={{ fontSize: '0.8rem' }}>{user.email}</td>
                <td>
                  <span className={`admin-badge ${user.provider === 'Google' ? 'admin-badge-green' : user.provider === 'Apple' ? 'admin-badge-yellow' : 'admin-badge-yellow'}`}>
                    {user.provider}
                  </span>
                </td>
                <td style={{ fontSize: '0.8rem' }}>{user.status}</td>
                <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
